import { useEffect, useRef, useState } from 'react';
import { type EventDraft, type EventTime } from '@project/event-format';
import { todayLocal } from './download';
import { EN_PREVIEW, type WeekPreviewMessages } from './preview-messages';

const DAY = 86400000;
const MIN_HEIGHT = 30;

export interface PreviewEvent {
  readonly id: string;
  readonly draft: EventDraft;
  readonly pending?: boolean;
}

/** UTC arithmetic is only a wall-clock coordinate system; it never converts event zones. */
function coordinate(time: EventTime): number {
  const value = time.kind === 'date' ? time.date : time.dateTime;
  if (!/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/.test(value)) return NaN;
  const date = new Date(value.length === 10 ? value + 'T00:00:00Z' : value + 'Z');
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, value.length) === value
    ? date.getTime()
    : NaN;
}

function monday(value: number): number {
  const date = new Date(value);
  date.setUTCHours(0, 0, 0, 0);
  return date.getTime() - ((date.getUTCDay() + 6) % 7) * DAY;
}

interface Block {
  event: PreviewEvent;
  top: number;
  height: number;
  column: number;
  columns: number;
}

export function dayBlocks(events: readonly PreviewEvent[], day: number): Block[] {
  const blocks = events
    .flatMap((event): Block[] => {
      if (event.draft.start.kind !== 'dateTime') return [];
      const start = coordinate(event.draft.start);
      const end = event.draft.end ? coordinate(event.draft.end) : start;
      if (
        !Number.isFinite(start) ||
        !Number.isFinite(end) ||
        end < start ||
        start >= day + DAY ||
        Math.max(end, start + 60000) <= day
      )
        return [];
      const top = Math.max(0, (start - day) / 60000);
      const height = Math.min(1440 - top, Math.max(MIN_HEIGHT, (end - day) / 60000 - top));
      return [{ event, top, height, column: 0, columns: 1 }];
    })
    .sort((a, b) => a.top - b.top || b.height - a.height);
  let group: Block[] = [];
  let ends: number[] = [];
  let groupEnd = -1;
  const finish = (): void => {
    group.forEach((block) => {
      block.columns = ends.length;
    });
  };
  for (const block of blocks) {
    if (block.top >= groupEnd) {
      finish();
      group = [];
      ends = [];
    }
    let column = ends.findIndex((end) => end <= block.top);
    if (column < 0) column = ends.length;
    block.column = column;
    ends[column] = block.top + block.height;
    groupEnd = Math.max(...ends);
    group.push(block);
  }
  finish();
  return blocks;
}

export function WeekPreview({
  events,
  focusDate,
  locale = 'en',
  messages = EN_PREVIEW,
  onSelect,
}: {
  readonly events: readonly PreviewEvent[];
  readonly focusDate?: string;
  readonly locale?: string;
  readonly messages?: WeekPreviewMessages;
  readonly onSelect: (id: string) => void;
}): React.JSX.Element {
  const focus = coordinate({ kind: 'date', date: focusDate ?? '' });
  const [week, setWeek] = useState(() =>
    monday(Number.isFinite(focus) ? focus : coordinate({ kind: 'date', date: todayLocal() })),
  );
  const scroll = useRef<HTMLDivElement>(null);
  const focusedEvent = events.find(
    (event) =>
      event.draft.start.kind === 'dateTime' &&
      event.draft.start.dateTime.slice(0, 10) === focusDate,
  );
  const focusedTime =
    focusedEvent?.draft.start.kind === 'dateTime'
      ? focusedEvent.draft.start.dateTime.slice(11, 16)
      : '';

  useEffect(() => {
    if (Number.isFinite(focus)) setWeek(monday(focus));
  }, [focus]);
  useEffect(() => {
    if (scroll.current) {
      const hour = Number(focusedTime.slice(0, 2));
      scroll.current.scrollTop =
        focusedTime && Number.isFinite(hour) ? Math.max(0, hour - 1) * 60 : 8 * 60;
    }
  }, [focus, focusedTime]);
  useEffect(() => {
    // A narrow preview scrolls horizontally. Keep the day being edited in view as well as
    // its hour; otherwise Friday/Sunday events appear to be missing off the right edge.
    if (!scroll.current || !Number.isFinite(focus) || monday(focus) !== week) return;
    const day = scroll.current.querySelector<HTMLElement>(
      `[data-date="${new Date(focus).toISOString().slice(0, 10)}"]`,
    );
    if (day) {
      const viewport = scroll.current.getBoundingClientRect();
      const bounds = day.getBoundingClientRect();
      scroll.current.scrollLeft +=
        bounds.left - viewport.left - (viewport.width - bounds.width) / 2;
    }
  }, [focus, week]);
  const days = Array.from({ length: 7 }, (_, index) => week + index * DAY);
  const dateLabel = (value: number, options: Intl.DateTimeFormatOptions): string =>
    new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(value);
  const clockLabel = (time: EventTime): string => {
    const value = coordinate(time);
    return Number.isFinite(value)
      ? dateLabel(value, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      : '';
  };
  const label = (event: PreviewEvent): string => {
    const { draft } = event;
    const when =
      draft.start.kind === 'date'
        ? messages.allDay
        : clockLabel(draft.start) +
          (draft.end?.kind === 'dateTime' ? '–' + clockLabel(draft.end) : '') +
          ' · ' +
          (draft.start.timeZone ?? messages.localTime);
    return (
      (event.pending ? messages.draft + ' · ' : '') +
      (draft.title || messages.untitled) +
      ' · ' +
      when +
      (draft.location ? ' · ' + draft.location : '') +
      (draft.recurrence ? ' · ' + messages.recurrence[draft.recurrence.frequency] : '')
    );
  };
  const eventButton = (event: PreviewEvent, style?: React.CSSProperties): React.JSX.Element => (
    <button
      key={event.id}
      type="button"
      className={'week-event' + (event.pending ? ' week-draft' : '')}
      style={style}
      title={label(event)}
      aria-label={label(event)}
      onClick={() => onSelect(event.id)}
    >
      <strong>
        {event.pending ? messages.draft + ' · ' : ''}
        {event.draft.title || messages.untitled}
      </strong>
      <span>
        {event.draft.start.kind === 'date' ? messages.allDay : clockLabel(event.draft.start)}
        {event.draft.end?.kind === 'dateTime' ? '–' + clockLabel(event.draft.end) : ''}
        {event.draft.recurrence ? ' ↻' : ''}
      </span>
      {event.draft.start.kind === 'dateTime' && (
        <span>{event.draft.start.timeZone ?? messages.localTime}</span>
      )}
    </button>
  );
  return (
    <section className="week-preview" aria-label={messages.region}>
      <div className="week-toolbar">
        <h2>{messages.heading}</h2>
        <div>
          <button
            type="button"
            aria-label={messages.previous}
            onClick={() => setWeek(week - 7 * DAY)}
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setWeek(monday(coordinate({ kind: 'date', date: todayLocal() })))}
          >
            {messages.today}
          </button>
          <button type="button" aria-label={messages.next} onClick={() => setWeek(week + 7 * DAY)}>
            ›
          </button>
        </div>
        <strong aria-live="polite">
          {dateLabel(week, { month: 'short', day: 'numeric' })} –{' '}
          {dateLabel(week + 6 * DAY, { month: 'short', day: 'numeric', year: 'numeric' })}
        </strong>
      </div>
      <p className="week-note">{messages.note}</p>
      <div className="week-scroll" ref={scroll} tabIndex={0} aria-label={messages.scroll}>
        <div className="week-grid">
          <div className="week-header">
            <span />
            {days.map((day) => (
              <strong key={day} data-date={new Date(day).toISOString().slice(0, 10)}>
                {dateLabel(day, { weekday: 'short', day: 'numeric' })}
              </strong>
            ))}
            <span>{messages.allDay}</span>
            {days.map((day) => (
              <div key={day}>
                {events
                  .filter(
                    ({ draft }) =>
                      draft.start.kind === 'date' &&
                      coordinate(draft.start) <= day &&
                      coordinate(draft.end ?? draft.start) >= day,
                  )
                  .map((event) => eventButton(event))}
              </div>
            ))}
          </div>
          <div className="week-hours">
            <div className="week-ruler">
              {Array.from({ length: 24 }, (_, hour) => (
                <span key={hour} style={{ top: hour * 60 }}>
                  {dateLabel(week + hour * 3600000, {
                    hour: '2-digit',
                    minute: '2-digit',
                    hourCycle: 'h23',
                  })}
                </span>
              ))}
            </div>
            {days.map((day) => (
              <div className="week-day" key={day}>
                {dayBlocks(events, day).map((block) =>
                  eventButton(block.event, {
                    position: 'absolute',
                    top: block.top,
                    height: block.height,
                    left: `calc(${(block.column * 100) / block.columns}% + 2px)`,
                    width: `calc(${100 / block.columns}% - 4px)`,
                  }),
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {events.length === 0 && <p className="week-note">{messages.empty}</p>}
    </section>
  );
}
